import { APPROVAL_CODE } from '../../domain/domainCodes.js'
import { useEffect, useState } from 'react'
import { Ban, CalendarClock, LockOpen, ShieldCheck, UserCheck, UserMinus } from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import { FormBody, Textarea } from '../../components/Form'
import DatePicker from '../../components/DatePicker'
import FormRow from '../../components/FormRow'
import Modal from '../../components/Modal'
import Tabs from '../../components/Tabs'
import { isEnglish } from '../../lib/i18n'
import { strings as S } from './strings'
import './modals.css'

const TABS = [
  { id: 'approve', label: S.status.tabs.approve, icon: UserCheck },
  { id: 'expiry', label: S.status.tabs.expiry, icon: CalendarClock },
  { id: 'locked', label: S.status.tabs.locked, icon: Ban },
  { id: 'resigned', label: S.status.tabs.resigned, icon: UserMinus },
]

/**
 * The stored value as the picker's own `YYYY-MM-DD`.
 *
 * All three dates on this screen used to be carried as the legacy flatpickr
 * format, `Y-m-d H:i:S` — which is not a format anything here can read. The
 * calendar parses ISO and gave up on the trailing time, so the expiry field
 * printed a value it could not preselect; and the two tabs that were still
 * plain text fields posted that string to endpoints binding `DateTime?` from a
 * JSON body, where a space in place of the `T` is not a date at all and the
 * request never reaches its handler.
 */
const asDateValue = (iso) => (iso ? iso.slice(0, 10) : '')

/**
 * A moment as the LOCAL day it falls on, `YYYY-MM-DD`.
 *
 * A lock end is a moment with an offset — the end of an administrator's chosen day, or
 * minutes after a failed password, stored in UTC — so cutting its ISO string would name
 * the UTC day, one off for part of every night here.
 */
const localDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const lockDay = (iso) => (iso ? localDay(new Date(iso)) : '')

/* Gregorian, Latin digits — as the statistics report prints its dates. `ar-SA` alone would
   print the Hijri calendar beside a picker that speaks Gregorian. */
const LOCK_MOMENT = new Intl.DateTimeFormat(isEnglish ? 'en-GB' : 'ar-u-ca-gregory-nu-latn', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

const lockMoment = (iso) => (iso ? LOCK_MOMENT.format(new Date(iso)) : '')

/**
 * `#edit_status_modal` — opened by the row's lock button.
 *
 * The legacy markup also carries a fifth pane (`user_unlock_tab`) that no nav item
 * targets, so it is unreachable and not rebuilt here.
 */
export default function UserStatusModal({ open, user, onClose, onSubmit }) {
  const [tab, setTab] = useState('approve')
  const [approved, setApproved] = useState(false)
  const [notes, setNotes] = useState('')
  const [expiry, setExpiry] = useState('')
  const [lockoutEnd, setLockoutEnd] = useState('')
  const [resignation, setResignation] = useState('')

  // `setStatusForm(User)` prefills, fetches the current expiry, and opens tab A.
  useEffect(() => {
    if (!open || !user) return
    setTab('approve')
    setApproved(user.isApproved === APPROVAL_CODE.APPROVED)
    setNotes('')
    setExpiry(asDateValue(user.activeUntil))
    setLockoutEnd(lockDay(user.lockoutEnd))
    setResignation(asDateValue(user.resignationDateConfirmed ?? user.resignationDate))
  }, [open, user])

  return (
    <Modal
      open={open}
      size="lg"
      title={S.row.status}
      icon={ShieldCheck}
      subtitle={user ? `${user.userName} — ${user.email}` : undefined}
      onClose={onClose}
    >
      <div className="user-modal__split">
        <Tabs items={TABS} value={tab} onChange={setTab} tone="light" ariaLabel={S.row.status} />

        <div className="user-modal__pane">
          {tab === 'approve' && (
            <FormBody>
              <Checkbox id="status-approved" checked={approved} onChange={setApproved}>
                <bdi>{S.status.approved}</bdi>
              </Checkbox>

              <FormRow label={S.status.notes}>
                {(id) => <Textarea id={id} rows={5} value={notes} onChange={setNotes} />}
              </FormRow>

              <div className="user-modal__actions">
                <Button onClick={() => onSubmit('approve', { approved, notes })}>
                  {S.status.update}
                </Button>
              </div>
            </FormBody>
          )}

          {tab === 'expiry' && (
            <form
              onSubmit={(event) => {
                event.preventDefault()
                onSubmit('expiry', { activeUntil: expiry })
              }}
            >
              <FormBody>
                <FormRow
                  label={S.status.expiryLabel}
                  hint={asDateValue(user?.activeUntil) || S.status.none}
                >
                  {/* Clearable, like the two below it: an empty value is what
                      the endpoint reads as "no expiry", and a calendar on its
                      own can only ever pick a day. */}
                  {(id) => (
                    <DatePicker id={id} clearable value={expiry} onChange={setExpiry} />
                  )}
                </FormRow>

                <div className="user-modal__actions">
                  <Button type="submit">{S.status.save}</Button>
                </div>
              </FormBody>
            </form>
          )}

          {tab === 'locked' && (
            <FormBody>
              {/* Where the account stands before anything is changed: the tab used to open
                  on an empty date whether or not the account was locked. */}
              <p className="user-modal__note">
                {user?.isLocked ? (
                  <>
                    <bdi>{S.status.lockedUntil}</bdi> <b>{lockMoment(user.lockoutEnd)}</b>
                  </>
                ) : (
                  <bdi>{S.status.notLocked}</bdi>
                )}
              </p>

              {/* Cleared rather than emptied by hand: a null here is what
                  UNLOCKS the account, so it has to stay reachable. A day already over is
                  unpickable — the lock runs to the end of the chosen day, so locking
                  "until" a past one would lock nothing. */}
              <FormRow label={S.status.lockoutLabel} hint={S.status.lockoutHint}>
                {(id) => (
                  <DatePicker
                    id={id}
                    clearable
                    value={lockoutEnd}
                    onChange={setLockoutEnd}
                    min={localDay(new Date())}
                  />
                )}
              </FormRow>

              {/* SETTING a lockout date and LIFTING one are two different things.
                  `SetUserUnlockedStatus` also resets the failed-attempt counter, which is
                  what "unlock this account" means — offered only when there is a lock to
                  lift, since on an active account it changed nothing and still said done. */}
              <div className={`user-modal__actions${user?.isLocked ? ' user-modal__actions--split' : ''}`}>
                {user?.isLocked && (
                  <Button variant="soft" onClick={() => onSubmit('unlock')}>
                    <LockOpen size={16} strokeWidth={1.75} />
                    {S.status.unlockNow}
                  </Button>
                )}
                <Button onClick={() => onSubmit('lock', { lockoutEnd })}>{S.status.update}</Button>
              </div>
            </FormBody>
          )}

          {tab === 'resigned' && (
            <FormBody>
              <p className="user-modal__note">
                <bdi>{S.status.fetchedResignation}</bdi>{' '}
                <b dir="ltr">{asDateValue(user?.resignationDate) || S.status.none}</b>
              </p>

              {/* Same reason as the lockout: clearing it is how a resignation
                  is called off. */}
              <FormRow label={S.status.resignationLabel} hint={S.status.resignationHint}>
                {(id) => (
                  <DatePicker id={id} clearable value={resignation} onChange={setResignation} />
                )}
              </FormRow>

              <div className="user-modal__actions">
                <Button onClick={() => onSubmit('resignation', { resignation })}>
                  {S.status.update}
                </Button>
              </div>
            </FormBody>
          )}
        </div>
      </div>
    </Modal>
  )
}
